import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import "../css/header.css";
import { useAuth } from "../security/AuthLogin";
import { updateUserData, updateProfilePic } from "../api/userApi";
import FullScreenSpinner from "./FullScreenSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faLandmark,
  faUsers,
  faTrophy,
  faTable,
  faHeart,
  faChartLine,
  faTools,
  faGavel,
  faPowerOff,
  faFolder,
  faBars,
  faTimes,
  faUser,
  faCog,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import { io, Socket } from "socket.io-client";

// 1) Definimos la interfaz de contadores
interface SoportesContadores {
  countFavoritos: number;
  countBajasEstadisticas: number;
  countSoportesTecnicos: number;
  countSoportesAdministrativos: number;
  countSoportesJuridicos: number;
}

// 2) Función para hacer fetch de /api/soportes/contadores-rapidos
async function fetchSoportesContadoresRapidos(): Promise<SoportesContadores> {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("No token");

  const resp = await fetch("/api/soportes/contadores-rapidos", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!resp.ok) {
    throw new Error(`Error al obtener contadores rápidos: ${resp.statusText}`);
  }
  const json = await resp.json();
  return json.data;
}

// Declaración global para usar el objeto google (para TypeScript)
declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

const Header: React.FC = () => {
  const { userName, userRole, logout, updateUserName, userData, isLoading } =
    useAuth();

  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "datos" | "preferencias" | "subusuarios"
  >("datos");
  const [profilepic, setProfilePic] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // 3) Estado local para contadores
  const [counts, setCounts] = useState<SoportesContadores>({
    countFavoritos: 0,
    countBajasEstadisticas: 0,
    countSoportesTecnicos: 0,
    countSoportesAdministrativos: 0,
    countSoportesJuridicos: 0,
  });

  // Referencia para el socket
  const socketRef = useRef<Socket | null>(null);

  // Formulario de datos de usuario
  const [formData, setFormData] = useState({
    nombre: "",
    emailnotificaciones: "",
    recibircorreos: true,
  });
  const [initialData, setInitialData] = useState(formData);

  const backendUrl = `${window.location.protocol}//${window.location.hostname}`;

  // Function to customize Google Translate dropdown
  const customizeGoogleTranslate = () => {
    // Try to find the Google Translate dropdown or its container
    const dropdown =
      document.querySelector(".goog-te-menu-frame") ||
      document.querySelector(".goog-te-menu2") ||
      document.querySelector("iframe.goog-te-menu-frame");

    if (dropdown) {
      // When dropdown is found, wait for its content to be loaded
      setTimeout(() => {
        // Try to access the menu items
        let menuItems;
        if (dropdown.tagName === "IFRAME") {
          try {
            // For iframe implementation
            const iframeDocument =
              (dropdown as HTMLIFrameElement).contentDocument ||
              (dropdown as HTMLIFrameElement).contentWindow?.document;
            if (iframeDocument) {
              menuItems = iframeDocument.querySelectorAll(
                ".goog-te-menu2-item"
              );
            }
          } catch (error) {
            console.error("Error accessing iframe content:", error);
          }
        } else {
          // Direct access
          menuItems = dropdown.querySelectorAll(".goog-te-menu2-item");
        }

        if (menuItems && menuItems.length) {
          // Add flags to menu items
          menuItems.forEach((item) => {
            const text = item.textContent?.trim().toLowerCase() || "";

            // Map language to country code
            let countryCode = "";
            if (text.includes("inglés") || text.includes("english"))
              countryCode = "us";
            else if (text.includes("español") || text.includes("spanish"))
              countryCode = "es";
            else if (text.includes("francés") || text.includes("french"))
              countryCode = "fr";
            else if (text.includes("alemán") || text.includes("german"))
              countryCode = "de";
            else if (text.includes("italiano") || text.includes("italian"))
              countryCode = "it";
            else if (text.includes("portugués") || text.includes("portuguese"))
              countryCode = "pt";
            else if (
              text.includes("catalán") ||
              text.includes("català") ||
              text.includes("catalan")
            )
              countryCode = "es-ct"; // Cataluña
            else if (
              text.includes("gallego") ||
              text.includes("galego") ||
              text.includes("galician")
            )
              countryCode = "es-ga"; // Galicia
            else if (
              text.includes("vasco") ||
              text.includes("euskera") ||
              text.includes("basque")
            )
              countryCode = "es-pv"; // País Vasco

            if (countryCode && !item.querySelector(".fi")) {
              const flagSpan = document.createElement("span");
              flagSpan.className = `fi fi-${countryCode}`;
              flagSpan.style.marginRight = "8px";
              item.insertBefore(flagSpan, item.firstChild);
            }
          });
        }
      }, 500); // Give time for the dropdown to fully render
    }
  };

  // 4) Conexión con Socket.IO y actualización de contadores
  useEffect(() => {
    const socket = io(window.location.origin, {
      path: "/socket.io",
      transports: ["websocket"],
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });

    socketRef.current = socket;

    fetchSoportesContadoresRapidos()
      .then((data) => {
        setCounts(data);
      })
      .catch((err) =>
        console.error("Error al obtener contadores iniciales (Header):", err)
      );

    socket.on("actualizarContadores", async () => {
      try {
        const data = await fetchSoportesContadoresRapidos();
        setCounts(data);
      } catch (err) {
        console.error(
          "Error actualizando contadores tras socket (Header):",
          err
        );
      }
    });

    return () => {
      socket.off("actualizarContadores");
      socket.disconnect();
    };
  }, []);

  

  // Carga el widget de Google Translate
  useEffect(() => {
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          /* pageLanguage: "es", */
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          // Aquí se incluyen solo los idiomas que deseas
          includedLanguages: "en,fr,de,it,pt,es",
        },
        "google_translate_element"
      );
    };

    const addGoogleTranslateScript = () => {
      if (!document.getElementById("google-translate-script")) {
        const script = document.createElement("script");
        script.id = "google-translate-script";
        script.type = "text/javascript";
        script.src =
          "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        document.body.appendChild(script);
      }
    };

    addGoogleTranslateScript();
  }, []);

  // -----------------------------
  // Personalización del menú de Google Translate
  // -----------------------------
  useEffect(() => {
    // Function to customize Google Translate dropdown
    const customizeDelayed = () => {
      // Try to find the Google Translate dropdown or its container
      const interval = setInterval(() => {
        // Look for the Google Translate dropdown (different selectors to be thorough)
        const dropdown =
          document.querySelector(".goog-te-menu-frame") ||
          document.querySelector(".goog-te-menu2") ||
          document.querySelector("iframe.goog-te-menu-frame");

        if (dropdown) {
          clearInterval(interval);
          customizeGoogleTranslate();
        }
      }, 300); // Check periodically

      // Cleanup interval after a reasonable time
      setTimeout(() => clearInterval(interval), 10000);
    };

    // Wait for Google Translate to initialize first
    const translateObserver = new MutationObserver((mutations) => {
      if (
        document.querySelector(
          "#google_translate_element select.goog-te-combo"
        ) ||
        document.querySelector(".skiptranslate")
      ) {
        customizeDelayed();
        translateObserver.disconnect();
      }
    });

    translateObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      translateObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Prevenir que el traductor se abra automáticamente en móvil
    const preventAutoOpen = () => {
      if (window.innerWidth <= 768) {
        // En móvil, prevenir clics accidentales y apertura automática
        const translateElement = document.querySelector(
          "#google_translate_element"
        );
        if (translateElement) {
          // Prevenir eventos de touch accidentales
          translateElement.addEventListener(
            "touchstart",
            (e) => {
              e.stopPropagation();
            },
            { passive: true }
          );

          // Agregar un pequeño delay antes de permitir clics
          let touchStartTime = 0;
          translateElement.addEventListener("touchstart", () => {
            touchStartTime = Date.now();
          });

          translateElement.addEventListener("touchend", (e) => {
            const touchDuration = Date.now() - touchStartTime;
            // Solo permitir clics que duren más de 150ms (clics intencionales)
            if (touchDuration < 150) {
              e.preventDefault();
              e.stopPropagation();
            }
          });
        }
      }
    };

    // Ejecutar cuando Google Translate esté listo
    const checkTranslateReady = setInterval(() => {
      if (
        document.querySelector(
          "#google_translate_element .goog-te-gadget-simple"
        )
      ) {
        preventAutoOpen();
        clearInterval(checkTranslateReady);
      }
    }, 500);

    // Limpiar el intervalo después de 10 segundos
    setTimeout(() => clearInterval(checkTranslateReady), 10000);

    return () => {
      clearInterval(checkTranslateReady);
    };
  }, []);

  useEffect(() => {
    // Add event listener for when the Google Translate dropdown is triggered
    const handleTranslateClick = () => {
      // Wait a moment for dropdown to appear
      setTimeout(customizeGoogleTranslate, 300);
    };

    // Helper function to find and attach event listeners to Google Translate elements
    const setupTranslateListeners = () => {
      const translateElement = document.querySelector(
        "#google_translate_element"
      );
      if (translateElement) {
        // Find clickable elements within the translate widget
        const clickableElements = translateElement.querySelectorAll(
          "select, .goog-te-gadget-simple"
        );
        clickableElements.forEach((element) => {
          element.addEventListener("click", handleTranslateClick);
        });
      }
    };

    // Set up initial listeners
    const initialSetupInterval = setInterval(() => {
      if (document.querySelector("#google_translate_element")) {
        setupTranslateListeners();
        clearInterval(initialSetupInterval);
      }
    }, 500);

    return () => {
      clearInterval(initialSetupInterval);

      // Clean up listeners
      const translateElement = document.querySelector(
        "#google_translate_element"
      );
      if (translateElement) {
        const clickableElements = translateElement.querySelectorAll(
          "select, .goog-te-gadget-simple"
        );
        clickableElements.forEach((element) => {
          element.removeEventListener("click", handleTranslateClick);
        });
      }
    };
  }, []);
  // -----------------------------
  // Fin de la personalización del menú de Google Translate
  // -----------------------------

  // Resto de la lógica: carga de userData, foto de perfil, etc.
  useEffect(() => {
    if (userData) {
      setFormData({
        nombre: userData.nombre,
        emailnotificaciones: userData.emailnotificaciones,
        recibircorreos: userData.recibircorreos,
      });
      setInitialData({
        nombre: userData.nombre,
        emailnotificaciones: userData.emailnotificaciones,
        recibircorreos: userData.recibircorreos,
      });

      const profilePicUrl = userData.profilepic
        ? `${backendUrl}${userData.profilepic}`
        : `${backendUrl}/images/default-user.png`;
      setProfilePic(profilePicUrl);
      setActiveTab("datos");
      setSelectedFile(null);
    }
  }, [userData, backendUrl]);

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isPopupOpen) setIsPopupOpen(false);
  };

  const handleProfilePicSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Lista de tipos MIME permitidos para imágenes
      const allowedImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
        "image/svg+xml",
      ];

      // Lista de extensiones permitidas (como respaldo)
      const allowedExtensions = [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".webp",
        ".bmp",
        ".svg",
      ];

      // Validar tipo MIME
      if (!allowedImageTypes.includes(file.type)) {
        alert(
          "Por favor, selecciona un archivo de imagen válido (JPG, JPEG, PNG, GIF, WebP, BMP, SVG)."
        );
        // Limpiar el input
        e.target.value = "";
        return;
      }

      // Validar extensión como medida adicional
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some((ext) =>
        fileName.endsWith(ext)
      );

      if (!hasValidExtension) {
        alert(
          "El archivo debe tener una extensión de imagen válida (.jpg, .jpeg, .png, .gif, .webp, .bmp, .svg)."
        );
        // Limpiar el input
        e.target.value = "";
        return;
      }

      // Validar tamaño del archivo (opcional - ejemplo: máximo 5MB)
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeInBytes) {
        alert(
          "El archivo es demasiado grande. El tamaño máximo permitido es 5MB."
        );
        // Limpiar el input
        e.target.value = "";
        return;
      }

      // Si pasa todas las validaciones, establecer el archivo
      setSelectedFile(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSave = async () => {
    try {
      if (selectedFile) {
        const response = await updateProfilePic(selectedFile, "upload");
        const updatedProfilePicUrl = `${backendUrl}${response.filePath}`;
        setProfilePic(updatedProfilePicUrl);
        setSelectedFile(null);
      }

      if (JSON.stringify(formData) !== JSON.stringify(initialData)) {
        await updateUserData(formData);
        updateUserName(formData.nombre);
      }
      togglePopup();
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      alert("Error al guardar los cambios.");
    }
  };

  const handleDeleteProfilePic = async () => {
    try {
      await updateProfilePic(undefined, "delete");
      const defaultPicUrl = `${backendUrl}/images/default-user.png`;
      setProfilePic(defaultPicUrl);
    } catch (error) {
      console.error("Error al eliminar la imagen:", error);
      alert("Error al eliminar la imagen de perfil.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Componente Badge para notificaciones
  const Badge = ({ number }: { number: number }) => {
    if (number <= 0) return null;
    return <span className="oclem-header__badge">{number}</span>;
  };

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  const totalNotifications = Object.values(counts).reduce(
    (acc, count) => acc + count,
    0
  );

  return (
    <header className="oclem-header">
      <div className="oclem-header__container">
        <button
          className={`oclem-header__mobile-toggle ${
            isMobileMenuOpen ? "oclem-header__mobile-toggle--active" : ""
          }`}
          onClick={toggleMobileMenu}
          aria-label="Menú"
        >
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
        </button>

        <div className="oclem-header__logo">
          <a href="/" className="oclem-header__logo-link">
            <img
              src="/images/logo_oclem.png"
              alt="Logo Grupo Oclem"
              className="oclem-header__logo-img"
            />
          </a>
        </div>

        <div className="oclem-header__actions">
          <div className="oclem-header__translate">
            <div id="google_translate_element"></div>
          </div>

          <div className="oclem-header__user-wrapper" onClick={togglePopup}>
            <div className="oclem-header__user">
              <span className="oclem-header__user-role">
                {userRole === "admin"
                  ? "Administrador"
                  : userRole === "tecnico"
                  ? "Técnico"
                  : "Asociado"}
              </span>
              <span className="oclem-header__user-name">{userName}</span>
              <div className="oclem-header__user-avatar">
                <img
                  src={profilepic}
                  alt={userName || "Usuario"}
                  className="oclem-header__user-img"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          className={`oclem-header__mobile-menu ${
            isMobileMenuOpen ? "oclem-header__mobile-menu--open" : ""
          }`}
        >
          <div className="oclem-header__mobile-header">
            <div className="oclem-header__mobile-user">
              <div className="oclem-header__mobile-avatar">
                <img
                  src={profilepic}
                  alt={userName || "Usuario"}
                  className="oclem-header__mobile-img"
                />
              </div>
              <div className="oclem-header__mobile-info">
                <span className="oclem-header__mobile-name">{userName}</span>
                <span className="oclem-header__mobile-role">
                  {userRole === "admin"
                    ? "Administrador"
                    : userRole === "tecnico"
                    ? "Técnico"
                    : "Cliente"}
                </span>
              </div>
            </div>
          </div>

          <nav className="oclem-header__mobile-nav">
            <ul className="oclem-header__mobile-list">
              {userRole === "admin" && (
                <>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/"
                      end
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faHome} /> Inicio
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/concursos"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faLandmark} /> Concursos
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/adjudicaciones"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/clientes"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faUsers} /> Clientes
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/usuarios"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faTable} /> Usuarios
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/diccionario-cpvs"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faBook} /> Diccionario CPVS
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-divider"></li>
                </>
              )}

              {userRole === "tecnico" && (
                <>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/"
                      end
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faHome} /> Inicio
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/concursos"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faLandmark} /> Concursos
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/adjudicaciones"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/diccionario-cpvs"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faBook} /> Diccionario CPVS
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-divider"></li>
                </>
              )}

              {userRole === "cliente" && (
                <>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/"
                      end
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faHome} /> Inicio
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/concursos"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faLandmark} /> Concursos
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-item">
                    <NavLink
                      to="/adjudicaciones"
                      onClick={toggleMobileMenu}
                      className={({ isActive }) =>
                        isActive
                          ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                          : "oclem-header__mobile-link"
                      }
                    >
                      <FontAwesomeIcon icon={faTrophy} /> Adjudicaciones
                    </NavLink>
                  </li>
                  <li className="oclem-header__mobile-divider"></li>
                </>
              )}

              <li className="oclem-header__mobile-item">
                <NavLink
                  to="/favoritos"
                  onClick={toggleMobileMenu}
                  className={({ isActive }) =>
                    isActive
                      ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                      : "oclem-header__mobile-link"
                  }
                >
                  <FontAwesomeIcon icon={faHeart} /> Favoritos
                  <Badge number={counts.countFavoritos} />
                </NavLink>
              </li>
              <li className="oclem-header__mobile-item">
                <NavLink
                  to="/bajas-estadisticas"
                  onClick={toggleMobileMenu}
                  className={({ isActive }) =>
                    isActive
                      ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                      : "oclem-header__mobile-link"
                  }
                >
                  <FontAwesomeIcon icon={faChartLine} /> Bajas estadísticas
                  <Badge number={counts.countBajasEstadisticas} />
                </NavLink>
              </li>
              <li className="oclem-header__mobile-item">
                <NavLink
                  to="/soportes-tecnicos"
                  onClick={toggleMobileMenu}
                  className={({ isActive }) =>
                    isActive
                      ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                      : "oclem-header__mobile-link"
                  }
                >
                  <FontAwesomeIcon icon={faTools} /> Soportes técnicos
                  <Badge number={counts.countSoportesTecnicos} />
                </NavLink>
              </li>
              <li className="oclem-header__mobile-item">
                <NavLink
                  to="/soportes-administrativos"
                  onClick={toggleMobileMenu}
                  className={({ isActive }) =>
                    isActive
                      ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                      : "oclem-header__mobile-link"
                  }
                >
                  <FontAwesomeIcon icon={faTools} /> Soportes administrativos
                  <Badge number={counts.countSoportesAdministrativos} />
                </NavLink>
              </li>
              <li className="oclem-header__mobile-item">
                <NavLink
                  to="/soportes-juridicos"
                  onClick={toggleMobileMenu}
                  className={({ isActive }) =>
                    isActive
                      ? "oclem-header__mobile-link oclem-header__mobile-link--active"
                      : "oclem-header__mobile-link"
                  }
                >
                  <FontAwesomeIcon icon={faGavel} /> Soportes jurídicos
                  <Badge number={counts.countSoportesJuridicos} />
                </NavLink>
              </li>
              <li className="oclem-header__mobile-divider"></li>
              <li className="oclem-header__mobile-item">
                <a
                  href="https://oclemconcursospublicos.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="oclem-header__mobile-link"
                >
                  <FontAwesomeIcon icon={faFolder} /> Clasificación
                </a>
              </li>
              <li className="oclem-header__mobile-item">
                <button
                  onClick={handleLogout}
                  className="oclem-header__mobile-button"
                >
                  <FontAwesomeIcon icon={faPowerOff} /> Cerrar sesión
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {isMobileMenuOpen && (
          <div
            className="oclem-header__overlay"
            onClick={toggleMobileMenu}
          ></div>
        )}

        {isPopupOpen && (
          <>
            <div
              className="oclem-header__popup-overlay"
              onClick={togglePopup}
            ></div>
            <div
              className="oclem-header__popup"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="oclem-header__popup-sidebar">
                <div className="oclem-header__popup-avatar">
                  <img
                    src={profilepic}
                    alt={userName || "Usuario"}
                    className="oclem-header__popup-img"
                  />
                </div>
                <div className="oclem-header__popup-name">{userName}</div>
                <div className="oclem-header__popup-role">
                  {userRole === "admin"
                    ? "Administrador"
                    : userRole === "tecnico"
                    ? "Técnico"
                    : "Asociado"}
                </div>
                <ul className="oclem-header__popup-tabs">
                  <li
                    onClick={() => setActiveTab("datos")}
                    className={`oclem-header__popup-tab ${
                      activeTab === "datos"
                        ? "oclem-header__popup-tab--active"
                        : ""
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} /> Mis datos
                  </li>
                  <li
                    onClick={() => setActiveTab("preferencias")}
                    className={`oclem-header__popup-tab ${
                      activeTab === "preferencias"
                        ? "oclem-header__popup-tab--active"
                        : ""
                    }`}
                  >
                    <FontAwesomeIcon icon={faCog} /> Preferencias
                  </li>
                  <li
                    onClick={handleLogout}
                    className="oclem-header__popup-tab"
                  >
                    <FontAwesomeIcon icon={faPowerOff} /> Cerrar sesión
                  </li>
                </ul>
              </div>

              <div className="oclem-header__popup-content">
                <button
                  onClick={togglePopup}
                  className="oclem-header__popup-close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>

                {activeTab === "datos" && userData && (
                  <div className="oclem-header__popup-section">
                    <h3 className="oclem-header__popup-title">Mis Datos</h3>
                    <div className="oclem-header__popup-form">
                      <div className="oclem-header__form-group">
                        <label className="oclem-header__form-label">
                          Nombre:
                        </label>
                        <input
                          type="text"
                          name="nombre"
                          value={formData.nombre || ""}
                          onChange={handleInputChange}
                          className="oclem-header__form-input"
                        />
                      </div>
                      <div className="oclem-header__form-group">
                        <label className="oclem-header__form-label">
                          Foto de perfil:
                        </label>
                        {profilepic ===
                        `${backendUrl}/images/default-user.png` ? (
                          <div className="oclem-header__file-input-container">
                            <input
                              type="file"
                              onChange={handleProfilePicSelect}
                              className="oclem-header__file-input"
                              id="profilePicUpload"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml"
                            />
                            <label
                              htmlFor="profilePicUpload"
                              className="oclem-header__file-label"
                            >
                              Seleccionar imagen
                            </label>
                            {selectedFile && (
                              <span className="oclem-header__file-name">
                                {selectedFile.name}
                              </span>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={handleDeleteProfilePic}
                            className="oclem-header__delete-photo"
                          >
                            Eliminar Foto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "preferencias" && userData && (
                  <div className="oclem-header__popup-section">
                    <h3 className="oclem-header__popup-title">Preferencias</h3>
                    <div className="oclem-header__popup-form">
                      <div className="oclem-header__form-group">
                        <label className="oclem-header__form-label">
                          Correo para notificaciones:
                        </label>
                        <input
                          type="email"
                          name="emailnotificaciones"
                          value={formData.emailnotificaciones || ""}
                          onChange={handleInputChange}
                          className="oclem-header__form-input"
                        />
                      </div>
                      <div className="oclem-header__form-group">
                        <label className="oclem-header__form-label">
                          Recibir correos de notificaciones:
                        </label>
                        <div className="oclem-header__toggle-group">
                          <label className="oclem-header__toggle">
                            <input
                              type="checkbox"
                              name="recibircorreos"
                              checked={formData.recibircorreos === true}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  recibircorreos: e.target.checked,
                                })
                              }
                              className="oclem-header__toggle-input"
                            />
                            <span className="oclem-header__toggle-slider"></span>
                          </label>
                          <span className="oclem-header__toggle-label">
                            {formData.recibircorreos ? "Sí" : "No"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSave}
                  className="oclem-header__popup-save"
                >
                  Guardar cambios
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
